import type { JsonObject, JsonValue } from './contracts';

const MAX_DEPTH = 5;
const MAX_ARRAY_LENGTH = 50;
const MAX_KEYS = 100;
const MAX_STRING_LENGTH = 2_000;

const truncate = (value: string, maxLength = MAX_STRING_LENGTH): string =>
  value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;

const normalizeValue = (value: unknown, depth: number, seen: WeakSet<object>): JsonValue => {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') {
    return typeof value === 'string' ? truncate(value) : value;
  }

  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (value instanceof URL) return value.toString();
  if (depth >= MAX_DEPTH || typeof value !== 'object' || value === undefined) return null;
  if (seen.has(value)) return '[Circular]';

  seen.add(value);

  if (Array.isArray(value)) {
    const normalized = value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((entry) => normalizeValue(entry, depth + 1, seen));
    seen.delete(value);
    return normalized;
  }

  const result: JsonObject = {};
  const entries = Object.entries(value as Record<string, unknown>).slice(0, MAX_KEYS);

  for (const [key, entry] of entries) {
    result[truncate(key, 100)] = normalizeValue(entry, depth + 1, seen);
  }

  seen.delete(value);
  return result;
};

export const sanitizeObject = (value: Record<string, unknown> | undefined): JsonObject => {
  if (!value) return {};
  const normalized = normalizeValue(value, 0, new WeakSet<object>());
  return normalized && !Array.isArray(normalized) && typeof normalized === 'object' ? normalized : {};
};

export const sanitizeString = (value: string, maxLength: number): string => truncate(value.trim(), maxLength);
